import React from "react"
import SbEditable from "storyblok-react"
import { StaticQuery, graphql } from "gatsby"

import Input from "../input-v2/Input"
import Select from "../input-v2/Select"
import Checkbox from "../input-v2/Checkbox"
import Telephone from "../input-v2/Telephone"
import TextArea from "../input-v2/TextArea"
import MultiSelect from "../input-v2/MultiSelect"
import Lookup from "../input-v2/Lookup"
import { StoryblokFormCopy } from "./StoryblokFormCopy"

const GetOptions = ({ slug }) => {
    return (
        <StaticQuery
            query={graphql`
                query {
                    allStoryblokDatasourceEntry {
                        edges {
                            node {
                                id
                                name
                                value
                                data_source
                            }
                        }
                    }
                    allStoryblokDatasource {
                        edges {
                            node {
                                name
                                slug
                            }
                        }
                    }
                }
            `}
            render={(data) => {
                let options = (
                    <React.Fragment>
                        <option></option>
                    </React.Fragment>
                );

                if (data && data.allStoryblokDatasourceEntry) {
                    if (Array.isArray(slug)) {
                        // Handle the case where slug is an array
                        options = (
                            <React.Fragment>
                                {slug.map((groupSlug) => {
                                    const dataSourceNode = data.allStoryblokDatasourceEntry.edges.find(
                                        (o) => o.node.data_source === groupSlug
                                    );
                                    if (dataSourceNode) {
                                        const dataSourceLabel = data.allStoryblokDatasource.edges.find((i) => i.node.slug === groupSlug)
                                        const groupName = dataSourceNode.node.name;
                                        return (
                                            <optgroup label={dataSourceLabel.node.name} key={dataSourceLabel.node.slug}>
                                                {data.allStoryblokDatasourceEntry?.edges.map((o) => {
                                                    if (o.node.data_source === groupSlug) {
                                                        return (
                                                            <option key={o.node.value} value={o.node.value}>
                                                                {o.node.name}
                                                            </option>
                                                        );
                                                    }
                                                    return null; // Skip other options
                                                })}
                                            </optgroup>
                                        );
                                    }
                                    return null; // Skip optgroup if dataSourceNode not found
                                })}
                            </React.Fragment>
                        );
                    } else {
                        // Handle the case where slug is a single value
                        options = (
                            <React.Fragment>
                                {data.allStoryblokDatasourceEntry?.edges.map((o) => {
                                    if (o.node.data_source === slug) {
                                        return (
                                            <option key={o.node.value} value={o.node.value}>
                                                {o.node.name}
                                            </option>
                                        );
                                    }
                                    return null; // Skip other options
                                })}
                            </React.Fragment>
                        );
                    }
                }
                return options;
            }}
        />
    );
};



function buildField(isDark, field, onValueChange, loading) {
    switch (field.type.toLowerCase()) {
        case "copy":
            return (
                <StoryblokFormCopy
                    title={field.title}
                    copy={field.copy}
                    loading={loading}
                    isDark={isDark}
                />
            )
        case "lookup":
            return (
                <Lookup
                    loading={loading}
                    isDark={isDark}
                    id={field.id}
                    key={field.id}
                    name={field.name}
                    label={field.label}
                    type={field.type}
                    placeholder={field.placeholder}
                    required={field.required}
                    hidden={field.hidden}
                    defaultValue={field.default}
                    value={field.value}
                    onChange={onValueChange}
                    isValid={field.valid}
                    lookup_options={field.lookup_options}
                // folder_name={field.folder_name}
                />
            )
        case "tel":
            return (
                <Telephone
                    loading={loading}
                    isDark={isDark}
                    id={field.id}
                    key={field.id}
                    name={field.name}
                    label={field.label}
                    type={field.type}
                    placeholder={field.placeholder}
                    required={field.required}
                    hidden={field.hidden}
                    defaultValue={field.default}
                    value={field.value}
                    countryCode={field.countryCode}
                    isoCountry={field.isoCountry}
                    onChange={onValueChange}
                    isValid={field.valid}
                />
            )
        case "text":
        case "email":
        case "password":
            return (
                <Input
                    hidden={field.hidden}
                    loading={loading}
                    isDark={isDark}
                    id={field.id}
                    key={field.id}
                    name={field.name}
                    label={field.label}
                    type={field.type}
                    placeholder={field.placeholder}
                    required={field.required}
                    defaultValue={field.default}
                    value={field.value}
                    onChange={onValueChange}
                    isValid={field.valid}
                    half={field.half}
                />
            )
        case "select":
            return (
                <Select
                    hidden={field.hidden}
                    loading={loading}
                    isDark={isDark}
                    key={field.id}
                    id={field.id}
                    name={field.name}
                    label={field.label}
                    required={field.required}
                    defaultValue={field.default}
                    value={field.value}
                    onChange={onValueChange}
                    isValid={field.valid}
                    half={field.half}
                >
                    {<GetOptions slug={field.options || field.grouped_options} />}
                </Select>
            )
        case "checkbox":
            return (
                <Checkbox
                    loading={loading}
                    isDark={isDark}
                    id={field.id}
                    key={field.id}
                    name={field.name}
                    label={field.label}
                    value={field.value ? true : false}
                    checked={field.value ? true : false}
                    required={field.required}
                    onChange={onValueChange}
                    isValid={field.valid}
                    richText={field.richText}
                />
            )
        case "textarea":
            return (
                <TextArea
                    loading={loading}
                    isDark={isDark}
                    id={field.id}
                    key={field.id}
                    name={field.name}
                    label={field.label}
                    type={field.type}
                    placeholder={field.placeholder}
                    required={field.required}
                    hidden={field.hidden}
                    defaultValue={field.default}
                    value={field.value}
                    onChange={onValueChange}
                    isValid={field.valid}
                />
            )
        case "dropdown":
            return (
                <Select
                    loading={loading}
                    isDark={isDark}
                    key={field.id}
                    id={field.id}
                    name={field.name}
                    label={field.label}
                    required={field.required}
                    hidden={field.hidden}
                    defaultValue={field.default}
                    value={field.value}
                    onChange={onValueChange}
                    isValid={field.valid}
                    half={field.half}
                >
                    {Array.isArray(field.dropdown_options) &&
                        field.dropdown_options.length > 0 &&
                        field.dropdown_options.map((item, idx) => (
                            <option
                                key={`${field.id}-dropdown-option-item-${idx}`}
                                value={item.value}
                            >
                                {item.label}
                            </option>
                        ))}
                </Select>
            )
        case "multiselect":

            return (
                <MultiSelect
                    loading={loading}
                    field={field}
                    isDark={isDark}
                    onValueChange={onValueChange}
                />
            )

        default:
            return null
    }
}

export const StoryblokFormField = ({ isDark, blok, onValueChange, loading }) => {
    const input = buildField(isDark, blok, onValueChange, loading)

    return (
        <SbEditable content={blok}>
            <>{input}</>
        </SbEditable>
    )
}
